/**
 * Best-effort persisted “app was installed” evidence for admin Settings Install.
 * Detection limit: requires a prior standalone open or Chromium `appinstalled` / BIP accept
 * (iOS Safari browser tab cannot see Home Screen PWAs otherwise).
 */

import { isAdminPwaInstalled } from './adminInstallOfferCooldown.js';

export const ADMIN_PWA_INSTALL_EVIDENCE_KEY = 'admin-pwa-install-evidence';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

interface RelatedApplication {
  readonly platform: string;
  readonly url?: string;
  readonly id?: string;
}

type NavigatorWithInstalledRelatedApps = Navigator & {
  readonly getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
};

export function hasPersistedAdminPwaInstallEvidence(
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const raw = storage.getItem(ADMIN_PWA_INSTALL_EVIDENCE_KEY);
  return raw === '1' || raw === 'true';
}

export function markAdminPwaInstallEvidence(
  storage: StorageLike = window.localStorage,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  storage.setItem(ADMIN_PWA_INSTALL_EVIDENCE_KEY, '1');
}

/** Stamp localStorage when display-mode / iOS standalone is already true. */
export function stampAdminPwaInstallEvidenceIfStandalone(
  storage: StorageLike = window.localStorage,
): void {
  if (isAdminPwaInstalled()) {
    markAdminPwaInstallEvidence(storage);
  }
}

async function hasInstalledRelatedAppsEvidence(): Promise<boolean> {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const getInstalled = (navigator as NavigatorWithInstalledRelatedApps)
    .getInstalledRelatedApps;
  if (typeof getInstalled !== 'function') {
    return false;
  }
  try {
    const apps = await getInstalled.call(navigator);
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}

/**
 * Positive install evidence: standalone display, persisted same-origin flag,
 * or `navigator.getInstalledRelatedApps` when supported.
 */
export async function hasPositiveAdminPwaInstallEvidence(
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isAdminPwaInstalled()) {
    return true;
  }
  if (hasPersistedAdminPwaInstallEvidence(storage)) {
    return true;
  }
  return hasInstalledRelatedAppsEvidence();
}
